/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @version 1.19.0
 * @since 1.0.0
 */
import { MutableRefObject } from 'react';

import invoke from 'lodash/invoke';

import { IOptionItemProps } from '../../../../../../components/common/Select/Select';
import { IExtFilterTranslation } from '../../../../../../modules/internationalization/types/pages/subscriberMobile';
import {
    ExtFilterAttrEnum,
    ExtFilterAttrTypeEnum,
    ExtFilterOperationsEnum,
    ExtFilterOperatorsEnum,
    IExtFilterItem,
    IExtFilterMetaDataItem,
    IExtFilterMetaItem,
    IExtFilterMetaMap,
} from '../../types/extFilterTypes';
import { IFilterConstructorItemOptions } from './ExtFilterConstructorItem';

const initialMetaItem: IExtFilterMetaItem = {
    attr: ExtFilterAttrEnum.ACCESS,
    attrType: ExtFilterAttrTypeEnum.SELECT,
    operators: [ExtFilterOperatorsEnum.EQ],
};

/**
 * @description Constructor utils props
 * @interface IFilterConstructUtilsProps
 * @property {IExtFilterMetaItem[]} extFilterMeta - external filters meta props
 * @property {IPageSubscriberTranslation} translation - locale
 */
interface IFilterConstructUtilsProps {
    extFilterMeta: IExtFilterMetaItem[];
    translation: IExtFilterTranslation;
}

const map: IExtFilterMetaMap = {
    [ExtFilterAttrEnum.ACCESS]: initialMetaItem,
    [ExtFilterAttrEnum.ACCOUNT]: initialMetaItem,
    [ExtFilterAttrEnum.CONTRACT_NUMBER]: initialMetaItem,
    [ExtFilterAttrEnum.DC_RULE]: initialMetaItem,
    [ExtFilterAttrEnum.LABEL]: initialMetaItem,
    [ExtFilterAttrEnum.NAME]: initialMetaItem,
    [ExtFilterAttrEnum.NUMBER_TYPE]: initialMetaItem,
    [ExtFilterAttrEnum.NUMBER]: initialMetaItem,
    [ExtFilterAttrEnum.PERSONAL_ACC]: initialMetaItem,
    [ExtFilterAttrEnum.POST]: initialMetaItem,
    [ExtFilterAttrEnum.STATUS]: initialMetaItem,
    [ExtFilterAttrEnum.TARIFF]: initialMetaItem,
};

/**
 * @description class for filter constructor
 */
export class ExtFilterConstructUtils {
    meta: IExtFilterMetaItem[];
    metaMap: IExtFilterMetaMap;
    translation: IExtFilterTranslation;

    constructor({ extFilterMeta, translation }: IFilterConstructUtilsProps) {
        this.meta = extFilterMeta;
        this.translation = translation;
        this.metaMap = this.getMetaMap(extFilterMeta);
    }

    /**
     * @description return options for FilterItem select
     */
    getItemOptions = (items: IExtFilterItem[]): IFilterConstructorItemOptions[] =>
        items.map((item: IExtFilterItem, index: number) =>
            this.getOptions(index, item.attr),
        );

    /**
     * @description return options for select
     */
    getOptions = (index: number, attr?: ExtFilterAttrEnum): IFilterConstructorItemOptions => {
        const options: IFilterConstructorItemOptions = {
            attrOptions: this.getSelectOptions('attr', this.meta),
            attrPlaceholder: this.translation.atribute,
        };
        if (index) {
            options.operationOptions = this.getSelectOptions('operation');
        }
        if (attr) {
            options.attrType = this.metaMap[attr].attrType;
            options.operatorsOptions = this.getSelectOptions('operators', this.metaMap[attr].operators);
            options.valuePlaceholder = this.translation.value;
            if (this.metaMap[attr].data) {
                options.valueOptions = this.getSelectOptions('value', this.metaMap[attr].data);
            }
        }
        return options;
    };

    /**
     * @description return new item for external filter
     */
    getNewItem = (
        attr: ExtFilterAttrEnum,
        item: IExtFilterItem,
        index: number,
    ): IExtFilterItem => {
        const newItem: IExtFilterItem = {
            ...item,
            attr: attr,
        };
        const itemOptions = this.getOptions(index, attr);
        if (itemOptions.operatorsOptions) {
            newItem.operator = (itemOptions.operatorsOptions)[0].value as ExtFilterOperatorsEnum;
        }
        if (itemOptions.valueOptions) {
            newItem.value = (itemOptions.valueOptions)[0].value.toString();
            newItem.valueName = (itemOptions.valueOptions)[0].label;
        } else {
            newItem.value = '';
            newItem.valueName = '';
        }
        return newItem;
    };

    private getMetaMap = (meta: IExtFilterMetaItem[]): IExtFilterMetaMap => {
        meta.forEach((metaItem: IExtFilterMetaItem) => {
            map[metaItem.attr] = metaItem;
        });
        return map;
    };

    private getSelectOptions = (
        type: string,
        options?: ExtFilterOperatorsEnum[] | IExtFilterMetaItem[] | IExtFilterMetaDataItem[],
    ): IOptionItemProps[] => {
        switch (type) {
            case 'attr':
                return (options as IExtFilterMetaItem[]).map((item: IExtFilterMetaItem) => {
                    return {
                        value: item.attr,
                        label: this.translation[item.attr],
                    };
                });
            case 'operation':
                return [
                    { value: ExtFilterOperationsEnum.AND, label: this.translation.AND },
                    { value: ExtFilterOperationsEnum.OR, label: this.translation.OR },
                ];
            case 'operators':
                if (options) {
                    return (options as ExtFilterOperatorsEnum[]).map((item: ExtFilterOperatorsEnum) => ({
                        value: item,
                        label: this.translation[item],
                    }));
                }
                return [];
            case 'value':
                return (options as IExtFilterMetaDataItem[]).map((item: IExtFilterMetaDataItem) => ({
                    value: item.key,
                    label: item.value,
                }));
            default:
                return [];
        }
    };
}

export const getSelected = (value?: string | null, options?: IOptionItemProps[]) => {
    if (!value || !options) {
        return undefined;
    }
    return options.find((item: IOptionItemProps) => item.value === value);
};

export const scrollToView = (tabindex?: number, ref?: MutableRefObject<HTMLDivElement>) => {
    const item = invoke(ref, 'current.querySelector', `.ext-filter__constructor-item[tabindex="${tabindex}"]`);
    if (item) {
        item.scrollIntoView({ behavior: 'smooth' });
    }
};
